/**
 * @engine/core - GameLoop
 * 
 * Main game loop with fixed and variable timesteps.
 * Handles frame callbacks and lifecycle.
 */

import { Time } from './Time';

/**
 * Loop callback types
 */
export interface LoopCallbacks {
  /** Called once per fixed timestep (physics) */
  fixedUpdate?: () => void;
  /** Called every frame before render */
  update?: () => void;
  /** Called every frame after update */
  lateUpdate?: () => void;
  /** Called every frame for rendering */
  render?: () => void;
}

/**
 * Loop state
 */
export enum LoopState {
  Stopped = 'stopped',
  Running = 'running',
  Paused = 'paused',
}

/**
 * GameLoop - Main engine loop
 * 
 * Manages:
 * - Fixed timestep for physics (fixedUpdate)
 * - Variable timestep for logic (update, lateUpdate)
 * - Render calls
 * - Pause/resume
 */
export class GameLoop {
  private _time: Time;
  private _state: LoopState = LoopState.Stopped;
  private _callbacks: LoopCallbacks = {};
  private _rafId: number | null = null;
  
  constructor(time?: Time) {
    this._time = time ?? new Time();
  }
  
  /** Get Time instance */
  get time(): Time {
    return this._time;
  }
  
  /** Current loop state */
  get state(): LoopState {
    return this._state;
  }
  
  /** Is loop running */
  get isRunning(): boolean {
    return this._state === LoopState.Running;
  }
  
  /** Is loop paused */
  get isPaused(): boolean {
    return this._state === LoopState.Paused;
  }
  
  /**
   * Set loop callbacks
   */
  setCallbacks(callbacks: LoopCallbacks): void {
    this._callbacks = callbacks;
  }
  
  /**
   * Start the game loop
   */
  start(): void {
    if (this._state === LoopState.Running) return;
    
    this._state = LoopState.Running;
    this._time.init(performance.now());
    this.scheduleFrame();
  }
  
  /**
   * Stop the game loop
   */
  stop(): void {
    this._state = LoopState.Stopped;
    
    if (this._rafId !== null) {
      clearTimeout(this._rafId); // TEMP: paired with setTimeout in scheduleFrame (revert to cancelAnimationFrame)
      this._rafId = null;
    }
  }
  
  /**
   * Pause the game loop (time stops, but loop continues for input)
   */
  pause(): void {
    if (this._state !== LoopState.Running) return;
    this._state = LoopState.Paused;
    this._time.timeScale = 0;
  }
  
  /**
   * Resume from pause
   */
  resume(): void {
    if (this._state !== LoopState.Paused) return;
    this._state = LoopState.Running;
    this._time.timeScale = 1;
  }
  
  /**
   * Run a single frame manually (for editor/debugging)
   */
  step(): void {
    const timestamp = performance.now();
    this._time.update(timestamp);
    
    // Force at least one fixed update
    this._callbacks.fixedUpdate?.();
    this._callbacks.update?.();
    this._callbacks.lateUpdate?.();
    this._callbacks.render?.();
  }
  
  /**
   * Schedule next frame
   */
  private scheduleFrame(): void {
    // TEMP (perf test): setTimeout instead of requestAnimationFrame to decouple the loop from the
    // raf/vsync cadence. setTimeout gives no timestamp, so stamp it ourselves. Revert to
    // requestAnimationFrame((timestamp) => this.frame(timestamp)) after measuring.
    this._rafId = setTimeout(() => this.frame(performance.now()), 0) as unknown as number;
  }
  
  /**
   * Main frame callback
   */
  private frame(timestamp: number): void {
    if (this._state === LoopState.Stopped) return;
    
    // Update time
    const fixedUpdates = this._time.update(timestamp);
    
    // Fixed updates (physics)
    if (this._state === LoopState.Running) {
      for (let i = 0; i < fixedUpdates; i++) {
        this._callbacks.fixedUpdate?.();
      }
    }
    
    // Variable updates
    if (this._state === LoopState.Running) {
      this._callbacks.update?.();
      this._callbacks.lateUpdate?.();
    }
    
    // Always render (even when paused, for UI responsiveness)
    this._callbacks.render?.();
    
    // Schedule next frame
    this.scheduleFrame();
  }
}
